import { IService, ServiceEvent } from '../base/BaseService';
import { TranscriptSpeaker } from '../../models/TranscriptSpeaker';
import { TranscriptVoiceMapping } from '../../types';
import type { Segment } from '../../models/Segment';

/**
 * Interface for DatoCMS speaker
 */
export interface DatoSpeaker {
  id: string;
  name: string;
  title?: string;
  bio?: string;
  voice?: string;
}

/**
 * Interface for speaker repository
 */
export interface SpeakerRepository {
  getStorySpeakers(storyId: string): Promise<{
    albumId: string;
    storySpeakers: DatoSpeaker[];
    albumSpeakers: DatoSpeaker[];
  }>;
  createSpeaker(name: string, title?: string, bio?: string, voice?: string, skipStoryAddition?: boolean): Promise<DatoSpeaker | null>;
  updateSpeaker(speakerId: string, updates: {
    name?: string;
    title?: string;
    bio?: string;
    voice?: string;
  }): Promise<DatoSpeaker | null>;
  addSpeakerReferenceToStory(speakerId: string, storyId: string): Promise<void>;
  addSpeakerReferenceToAlbum(speakerId: string, albumId: string): Promise<void>;
}

/**
 * Events emitted by the SpeakerService
 */
export enum SpeakerEvent {
  SPEAKERS_LOADED = 'speakers_loaded',
  SPEAKER_ADDED = 'speaker_added',
  SPEAKER_UPDATED = 'speaker_updated',
  SPEAKER_REMOVED = 'speaker_removed',
  SPEAKER_ASSIGNED = 'speaker_assigned',
  MAPPINGS_UPDATED = 'mappings_updated',
  LOADING_CHANGED = 'loading_changed',
  ERROR = 'error'
}

/**
 * Make SpeakerEvent compatible with ServiceEvent
 */
// Enum extends implicitly

/**
 * The result of a speaker assignment operation
 */
export interface SpeakerAssignmentResult {
  success: boolean;
  speakerLabel: string;
  elementIds: string[];
  error?: string;
}

/**
 * Interface for the speaker service
 */
export interface ISpeakerService extends IService<SpeakerEvent> {
  /**
   * Initializes the speaker service with repository and IDs
   */
  initialize(
    speakerRepository: SpeakerRepository,
    storyId: string | null,
    albumId: string | null
  ): void;
  
  /**
   * Loads speakers for a story
   */
  loadSpeakers(storyId: string): Promise<void>;
  
  /**
   * Loads speaker mappings from a mapping object
   */
  loadSpeakerMappings(mappings: TranscriptVoiceMapping): void;
  
  /**
   * Gets a speaker color by its Dato CMS ID
   */
  getSpeakerColor(speakerLabel: string): string;
  
  /**
   * Gets a speaker name by its anonymous label
   */
  getSpeakerName(speakerLabel: string): string | undefined;
  
  /**
   * Creates a new speaker
   * @param name Speaker name
   * @param title Optional speaker title
   * @param options Optional location options (where to add the speaker)
   */
  createSpeaker(
    name: string,
    title?: string,
    bio?: string,
    voice?: string,
    skipStoryAddition?: boolean
  ): Promise<DatoSpeaker | null>;
  
  /**
   * Updates a speaker's local properties (color)
   */
  updateSpeaker(
    speakerLabel: string,
    updates: { name?: string; color?: string }
  ): boolean;

  /**
   * Updates a speaker's properties in DatoCMS
   */
  updateSpeakerProperties(
    datoSpeakerId: string,
    updates: {
      name?: string;
      title?: string;
      bio?: string;
      voice?: string;
    }
  ): Promise<boolean>;
  
  /**
   * Removes a speaker from the local cache
   */
  removeSpeaker(speakerLabel: string): boolean;

 
  /**
   * Gets all speakers
   */
  getSpeakers(): TranscriptSpeaker[];
  
  /**
   * Gets the current speaker mappings
   */
  getSpeakerMappings(): TranscriptVoiceMapping | null;
  
  /**
   * Directly update a single speaker mapping
   */
  updateSpeakerMapping(speakerLabel: string, datoSpeakerId: string): Promise<boolean>;

  
  /**
   * Reassigns an anonymous speaker ID to another anonymous speaker ID
   */
  reassignAnonymousSpeaker(
    fromSpeakerLabel: string,
    toSpeakerLabel: string,
    elementIds: string[]
  ): Promise<SpeakerAssignmentResult>;
  
  /**
   * Save speaker mappings to transcriptVoices.json with retry mechanism
   */
  saveSpeakerMappings(maxRetries?: number): Promise<boolean>;
  
  /**
   * Gets all speakers from transcript, DatoCMS, etc. and organizes them by category
   */
  getAllSpeakers(container: any, editorStore: any): {
    mapped: Array<any>;
    unmapped: Array<any>;
    story: Array<any>;
    album: Array<any>;
  };
  
  /**
   * Whether the service is currently loading data
   */
  readonly isLoading: boolean;
  
  /**
   * The story ID
   */
  readonly storyId: string | null;
  
  /**
   * The album ID
   */
  readonly albumId: string | null;
}